import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
    Alert,
    Tooltip,
    CircularProgress
} from '@mui/material';
import { ReactComponent as WarningImage } from '../../../assets/warning.svg';
import InfoIcon from '@mui/icons-material/Info';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { PaginatedResultsTable } from './PaginatedResultsTable';
import HandlerRegistryModal from 'components/HandlerRegistry/HandlerRegistryModal';
import { HandlerRegistryHandler } from 'components/HandlerRegistry/types';
import CreateNewHandlerModal from './CreateNewHandlerModal';
import { StyledSelect } from './SharedComponents';
import { HandlerOption, HandlerRegistryEntry, MappingRow, CreateHandlerFormState, SavedRule, ServiceMappingTabProps } from './types';
import { initialMappingData, initialHandlerTypeOptions, initialHandlerOptions, initialRegistry, HANDLER_CATEGORY_OPTIONS, initialCreateHandlerForm } from './constants';
import { getRiskColor, slugify, validateCreateHandlerForm, mapDtoListToSavedRules } from './helpers';
import { updateRuleConfig, getRuleConfigs, updateServiceMap } from 'service/approvalWorkflow';
import LoaderComponent from 'components/Loader';

const STATUS_OPTIONS: HandlerOption[] = [
    { value: 'MAPPED', label: 'Mapped' },
    { value: 'UNMAPPED', label: 'Unmapped' }
];

const ServiceMappingTab: React.FC<ServiceMappingTabProps> = ({ savedRules, mappings: externalMappings, onMappingsChange, onCompletionChange, onSaveAndConfigure }) => {
    const [mappings, setMappings] = useState<MappingRow[]>(() => externalMappings && externalMappings.length > 0 ? externalMappings : initialMappingData);
    const [handlerTypeOptions, setHandlerTypeOptions] = useState<HandlerOption[]>(initialHandlerTypeOptions);
    const [handlerOptions, setHandlerOptions] = useState<HandlerOption[]>(initialHandlerOptions);
    const [registry, setRegistry] = useState<HandlerRegistryEntry[]>(initialRegistry);
    const [isSavingMappings, setIsSavingMappings] = useState(false);

    const [rowErrors, setRowErrors] = useState<Record<number, string>>({});
    const [rowToDelete, setRowToDelete] = useState<MappingRow | null>(null);
    const [registryOpen, setRegistryOpen] = useState(false);
    const [registryCategory, setRegistryCategory] = useState('all');
    const [registrySearch, setRegistrySearch] = useState('');
    const [loader, setLoader] = useState<boolean>(false);
    const [createHandlerOpen, setCreateHandlerOpen] = useState(false);
    const [newHandlerForm, setNewHandlerForm] = useState<CreateHandlerFormState>(initialCreateHandlerForm);
    const [createHandlerErrors, setCreateHandlerErrors] = useState<{ handlerType?: string; handlerName?: string }>({});

    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success'
    });
    useEffect(() => {
        let isMounted = true;
        if (savedRules && savedRules.length > 0) {
            setMappings((prev) => {
                return savedRules.map((rule) => {
                    const existing = prev.find((m) => m.ewsRule.toLowerCase() === rule.ruleName.toLowerCase() || m.id === rule.id);
                    const handlerType = existing?.handlerType || rule.handlerType || 'pepwise';
                    const handler = existing?.handler || rule.handler || 'pepwise_db';
                    const status = existing?.status || 'MAPPED';
                    return {
                        id: rule.id,
                        ewsRule: rule.ruleName,
                        risk: (rule.riskLevel || 'medium').toUpperCase() as any,
                        handlerType: handlerType,
                        handler: handler,
                        status: status as 'MAPPED' | 'UNMAPPED',
                        isEditing: existing?.isEditing || false,
                        draftHandlerType: existing?.draftHandlerType || handlerType,
                        draftHandler: existing?.draftHandler || handler,
                        draftStatus: existing?.draftStatus || status
                    };
                });
            });
        } else {
            getRuleConfigs().then((resp) => {
                const data = resp?.data || resp;
                if (isMounted && Array.isArray(data) && data.length > 0) {
                    const fetched = mapDtoListToSavedRules(data);
                    setMappings((prev) => {
                        return fetched.map((rule) => {
                            const existing = prev.find((m) => m.ewsRule.toLowerCase() === rule.ruleName.toLowerCase() || m.id === rule.id);
                            const handlerType = existing?.handlerType || rule.handlerType || 'pepwise';
                            const handler = existing?.handler || rule.handler || 'pepwise_db';
                            const status = existing?.status || 'MAPPED';
                            return {
                                id: rule.id,
                                ewsRule: rule.ruleName,
                                risk: (rule.riskLevel || 'LOW').toUpperCase() as any,
                                handlerType: handlerType,
                                handler: handler,
                                status: status as 'MAPPED' | 'UNMAPPED',
                                isEditing: false,
                                draftHandlerType: existing?.draftHandlerType || handlerType,
                                draftHandler: existing?.draftHandler || handler,
                                draftStatus: existing?.draftStatus || status
                            };
                        });
                    });
                }
            }).catch((err) => console.log('ServiceMappingTab getRuleConfigs note:', err));
        }
        return () => { isMounted = false; };
    }, [savedRules]);

    useEffect(() => {
        onMappingsChange?.(mappings);
    }, [mappings, onMappingsChange]);

    const handleEditRow = (id: number) => {
        setMappings((prev) => prev.map((row) => (
            row.id === id
                ? {
                    ...row,
                    isEditing: true,
                    draftHandlerType: row.draftHandlerType || row.handlerType || 'pepwise',
                    draftHandler: row.draftHandler || row.handler || 'pepwise_db',
                    draftStatus: row.draftStatus || row.status || 'MAPPED'
                }
                : row
        )));
        setRowErrors((prev) => ({ ...prev, [id]: '' }));
    };

    const handleDraftChange = (id: number, key: 'draftHandlerType' | 'draftHandler' | 'draftStatus', value: string) => {
        setMappings((prev) => prev.map((row) => (row.id === id ? { ...row, [key]: value } : row)));
    };

    const handleCancelEdit = (id: number) => {
        setMappings((prev) => prev.map((row) => (
            row.id === id
                ? { ...row, isEditing: false, draftHandlerType: row.handlerType || 'pepwise', draftHandler: row.handler || 'pepwise_db', draftStatus: row.status || 'MAPPED' }
                : row
        )));
        setRowErrors((prev) => ({ ...prev, [id]: '' }));
    };

    const handleConfirmEdit = async (id: number) => {
        const row = mappings.find((r) => r.id === id);
        if (!row) return;

        const selectedType = row.draftHandlerType || 'pepwise';
        const selectedHandler = row.draftHandler || 'pepwise_db';
        const selectedStatus: 'MAPPED' | 'UNMAPPED' = row.draftStatus === 'UNMAPPED' ? 'UNMAPPED' : 'MAPPED';

        const correspondingSavedRule = savedRules?.find((r) => r.id === id || r.ruleName.toLowerCase() === row.ewsRule.toLowerCase());
        if (correspondingSavedRule?.backendId) {
            try {
                await updateServiceMap(row.ewsRule, {
                    Handler_Type: selectedType,
                    Handler: selectedHandler,
                    Risk_Level: row?.risk,
                    Handler_status: selectedStatus,
                });
            } catch (err) {
                console.log('Error updating service map:', err);
            }
        }

        setMappings((prev) => prev.map((r) => (
            r.id === id
                ? { ...r, handlerType: selectedType, handler: selectedHandler, status: selectedStatus, isEditing: false }
                : r
        )));
        setRowErrors((prev) => ({ ...prev, [id]: '' }));
        setSnackbar({ open: true, message: `"${row.ewsRule}" mapped successfully.`, severity: 'success' });
    };

    const handleRequestDelete = (row: MappingRow) => setRowToDelete(row);

    const handleConfirmDelete = async () => {
        if (!rowToDelete) return;

        if (rowToDelete?.ewsRule) {
             setLoader(true)
            try {
                await updateServiceMap(rowToDelete.ewsRule, {
                     action: 'delete',
                });
            } catch (err) {
                console.log('Error updating service map:', err);
            }
        }


        setMappings((prev) =>
                prev.map((r) =>
                    r.id === rowToDelete.id
                        ? {
                              ...r,
                              handlerType: '',
                              handler: '',
                              status: 'UNMAPPED',
                              isEditing: false,
                          }
                        : r
                )
            );
        setSnackbar({ open: true, message: `Mapping for "${rowToDelete.ewsRule}" was removed.`, severity: 'success' });
        setRowToDelete(null);
        setLoader(false)
    };

    const handleSaveAndConfigure = async () => {
        if (mappings.length === 0) {
            setSnackbar({ open: true, message: 'Please select or create at least one rule to configure.', severity: 'error' });
            return;
        }

        setIsSavingMappings(true);
        try {
            await Promise.all(
                mappings.map((m) => {
                    const rule = savedRules?.find((r) => r.id === m.id || r.ruleName.toLowerCase() === m.ewsRule.toLowerCase());
                    if (rule?.backendId) {
                        return updateRuleConfig(rule.backendId, {
                            Handler_Type: m.handlerType || 'pepwise',
                            Handler: m.handler || 'pepwise_db'
                        });
                    }
                    return Promise.resolve();
                })
            );
        } catch (err) {
            console.log('Error persisting handler mappings to backend:', err);
        } finally {
            setIsSavingMappings(false);
        }

        onCompletionChange?.(true);
        onSaveAndConfigure?.(mappings);
    };

    const handleCreateHandlerFieldChange = <K extends keyof CreateHandlerFormState>(key: K, value: CreateHandlerFormState[K]) => {
        setNewHandlerForm((prev) => ({ ...prev, [key]: value }));
    };

    const handleCancelCreateHandler = () => {
        setCreateHandlerOpen(false);
        setNewHandlerForm(initialCreateHandlerForm);
        setCreateHandlerErrors({});
    };

    const handleCreateHandler = () => {
        const errors = validateCreateHandlerForm(newHandlerForm);
        const typeLabel = newHandlerForm.handlerType.trim();
        const nameLabel = newHandlerForm.handlerName.trim();

        if (!errors.handlerName && !errors.handlerType
            && registry.some((r) => r.handlerType.toLowerCase() === typeLabel.toLowerCase() && r.handler.toLowerCase() === nameLabel.toLowerCase())) {
            errors.handlerName = 'This Handler Type and Handler combination already exists.';
        }

        setCreateHandlerErrors(errors);
        if (Object.keys(errors).length > 0) return;

        const typeValue = slugify(typeLabel);
        const handlerValue = slugify(nameLabel);

        setHandlerTypeOptions((prev) => (prev.some((o) => o.value === typeValue) ? prev : [...prev, { value: typeValue, label: typeLabel }]));
        setHandlerOptions((prev) => (prev.some((o) => o.value === handlerValue) ? prev : [...prev, { value: handlerValue, label: nameLabel }]));
        setRegistry((prev) => [...prev, {
            id: prev.length ? Math.max(...prev.map((r) => r.id)) + 1 : 1,
            handlerType: typeLabel,
            handler: nameLabel,
            iconLabel: typeLabel.slice(0, 2).toUpperCase(),
            description: newHandlerForm.description.trim() || typeLabel,
            category: newHandlerForm.executor || 'other',
            version: newHandlerForm.version || 'v1.0.0',
            onboarded: false
        }]);

        setSnackbar({ open: true, message: `Handler "${nameLabel}" created successfully.`, severity: 'success' });
        handleCancelCreateHandler();
    };

    const handleOnboardHandler = (id: number) => {
        const entry = registry.find((r) => r.id === id);
        setRegistry((prev) => prev.map((r) => (r.id === id ? { ...r, onboarded: true } : r)));
        if (entry) {
            setSnackbar({ open: true, message: `"${entry.handler}" onboarded successfully.`, severity: 'success' });
        }
    };

    const registryHandlers: HandlerRegistryHandler[] = registry.map((entry) => ({
        id: entry.id,
        iconLabel: entry.iconLabel,
        name: entry.handler,
        description: entry.description,
        tag: entry.handlerType,
        version: entry.version,
        category: entry.category
    }));
    const onboardedHandlerIds = registry.filter((entry) => entry.onboarded).map((entry) => entry.id);

    const serviceMappingColumns = [
        { name: 'EWS RULE', key: 'ewsRule', minWidth: 130 },
        {
            name: 'RISK',
            key: 'risk',
            minWidth: 120,
            formatter: ({ row }: { row: MappingRow }) => (
                <Box sx={{
                    backgroundColor: getRiskColor(row.risk),
                    color: '#fff',
                    px: 1.5,
                    py: 0.2,
                    borderRadius: '12px',
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    display: 'inline-block'
                }}>
                    {row.risk}
                </Box>
            )
        },
        {
            name: 'HANDLER TYPE',
            key: 'handlerType',
            minWidth: 180,
            formatter: ({ row }: { row: MappingRow }) => (
                row.isEditing ? (
                    <StyledSelect
                        label="Select"
                        hideLabel
                        value={row.draftHandlerType || 'pepwise'}
                        onChange={(val) => handleDraftChange(row.id, 'draftHandlerType', val)}
                        options={handlerTypeOptions}
                        error={!!rowErrors[row.id]}
                    />
                ) : (
                    <Typography sx={{ fontSize: '0.875rem', color: row.handlerType ? '#333' : '#a0aab4' }}>
                        {row.handlerType
                            ? (handlerTypeOptions.find((o) => o.value.toLowerCase() === row.handlerType.toLowerCase())?.label || row.handlerType)
                            : '—'}
                    </Typography>
                )
            )
        },
        {
            name: 'HANDLER',
            key: 'handler',
            minWidth: 140,
            formatter: ({ row }: { row: MappingRow }) => (
                row.isEditing ? (
                    <StyledSelect
                        label="Select"
                        hideLabel
                        value={row.draftHandler || 'pepwise_db'}
                        onChange={(val) => handleDraftChange(row.id, 'draftHandler', val)}
                        options={handlerOptions}
                        error={!!rowErrors[row.id]}
                        helperText={rowErrors[row.id] || undefined}
                    />
                ) : (
                    <Typography sx={{ fontSize: '0.875rem', color: row.handler ? '#333' : '#a0aab4' }}>
                        {row.handler
                            ? (handlerOptions.find((o) => o.value.toLowerCase() === row.handler.toLowerCase())?.label || row.handler)
                            : '—'}
                    </Typography>
                )
            )
        },
        {
            name: 'STATUS',
            key: 'status',
            minWidth: 120,
            formatter: ({ row }: { row: MappingRow }) => (
                row.isEditing ? (
                    <StyledSelect
                        label="Select"
                        hideLabel
                        value={row.draftStatus || row.status || 'MAPPED'}
                        onChange={(val) => handleDraftChange(row.id, 'draftStatus', val)}
                        options={STATUS_OPTIONS}
                    />
                ) : (
                    <Box sx={{
                        backgroundColor: row.status === 'MAPPED' ? '#c8f7c5' : '#fce8b2',
                        color: row.status === 'MAPPED' ? '#2e7d32' : '#f57f17',
                        px: 2,
                        py: 0.5,
                        borderRadius: '16px',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        display: 'inline-block',
                        lineHeight: '18px !important',
                    }}>
                        {row.status}
                    </Box>
                )
            )
        },
        {
            name: 'ACTIONS',
            key: 'actions',
            minWidth: 130,
            sortable: false,
            formatter: ({ row }: { row: MappingRow }) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {row.isEditing ? (
                        <>
                            <CloseIcon
                                onClick={() => handleCancelEdit(row.id)}
                                sx={{ color: '#e53935', fontSize: '1.2rem', cursor: 'pointer' }}
                            />
                            <CheckIcon
                                onClick={() => handleConfirmEdit(row.id)}
                                sx={{ color: '#39ACE7', fontSize: '1.2rem', cursor: 'pointer' }}
                            />
                        </>
                    ) : (
                        <>
                            <EditIcon
                                onClick={() => handleEditRow(row.id)}
                                sx={{ color: '#39ACE7', fontSize: '1.1rem', cursor: 'pointer' }}
                            />
                            <DeleteIcon
                                onClick={() => handleRequestDelete(row)}
                                sx={{ color: '#e53935', fontSize: '1.1rem', cursor: 'pointer' }}
                            />
                        </>
                    )}
                </Box>
            )
        }
    ];

    return (
        <Box sx={{ mt: 3 }}>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2,
                mb: 3
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <InfoIcon sx={{ color: '#a0aab4', fontSize: '1.2rem', mr: 1 }} />
                    <Typography sx={{ fontStyle: 'italic', color: '#a0aab4', fontSize: '0.9rem' }}>
                        This section is to configure service mapping.
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Tooltip title="Handler registry is currently disabled.">
                        <span>
                            <Button
                                variant="outlined"
                                disabled={true}
                                sx={{
                                    color: '#39ACE7',
                                    borderColor: '#39ACE7',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    '&.Mui-disabled': {
                                        color: '#64748b !important',
                                        borderColor: '#94a3b8 !important',
                                        backgroundColor: '#f1f5f9 !important',
                                        cursor: 'not-allowed',
                                    }
                                }}
                            >
                                VIEW HANDLER REGISTRY
                            </Button>
                        </span>
                    </Tooltip>
                    <Tooltip title="Disabled until official handler registry and integrations are onboarded into the framework.">
                        <span>
                            <Button
                                variant="contained"
                                disabled={true}
                                onClick={() => setCreateHandlerOpen(true)}
                                sx={{
                                    backgroundColor: '#39ACE7',
                                    color: '#fff',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    '&:hover': {
                                        backgroundColor: '#2c8ebb'
                                    },
                                    '&.Mui-disabled': {
                                        backgroundColor: '#94a3b8 !important',
                                        color: '#ffffff !important',
                                        cursor: 'not-allowed',
                                    }
                                }}
                            >
                                CREATE NEW HANDLER
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
            </Box>

            <Box sx={{
                width: '100%',
                mb: 5,
                '.MuiDataGrid-columnHeaders': { backgroundColor: '#003771', color: '#fff' },
                '.rdg-cell': { borderInlineEnd: 'none' }
            }}>
                 {
                    loader ?  
                            <LoaderComponent size={32} /> 
                                    : 
                            <PaginatedResultsTable
                                tableType="ews-service-mapping"
                                columns={serviceMappingColumns}
                                data={mappings}
                            />
                 }
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 4, mt: -2 }}>
                <Button
                    variant="contained"
                    onClick={handleSaveAndConfigure}
                    disabled={isSavingMappings || !mappings.some((m) => m.status === 'MAPPED')}
                    startIcon={isSavingMappings ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : undefined}
                    sx={{
                        backgroundColor: '#39ACE7',
                        color: '#fff',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        px: 3,
                        py: 1,
                        lineHeight: '18px !important',
                        borderRadius: '4px',
                        '&:hover': {
                            backgroundColor: '#2c8ebb'
                        },
                        '&.Mui-disabled': { backgroundColor: '#39ACE799', color: '#fff' }
                    }}
                >
                    {isSavingMappings ? 'SAVING...' : 'SAVE AND CONFIGURE'}
                </Button>
            </Box>

            {/* View Handler Registry modal */}
            <HandlerRegistryModal
                isOpen={registryOpen}
                onClose={() => setRegistryOpen(false)}
                handlers={registryHandlers}
                categories={HANDLER_CATEGORY_OPTIONS}
                selectedCategory={registryCategory}
                onCategoryChange={setRegistryCategory}
                searchValue={registrySearch}
                onSearchChange={setRegistrySearch}
                onboardedHandlerIds={onboardedHandlerIds}
                onOnboard={handleOnboardHandler}
            />

            {/* Create New Handler modal */}
            <CreateNewHandlerModal
                isOpen={createHandlerOpen}
                onCancel={handleCancelCreateHandler}
                onSave={handleCreateHandler}
                form={newHandlerForm}
                errors={createHandlerErrors}
                handlerTypeOptions={handlerTypeOptions}
                onFieldChange={handleCreateHandlerFieldChange}
            />

            {/* Delete confirmation dialog */}
            <Dialog
                open={!!rowToDelete}
                onClose={() => setRowToDelete(null)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: '#FFFFFF',
                        color: '#1e293b',
                        width: '560px !important',
                        maxWidth: '100%',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)'
                    }
                }}
            >
                {/* Header */}
                <DialogTitle
                    sx={{
                        bgcolor: 'primary.main',
                        color: '#FFFFFF',
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    <Typography sx={{ fontSize: '18px', fontWeight: 600, color: '#FFFFFF' }}>
                        Remove Mapping
                    </Typography>
                    <IconButton onClick={() => setRowToDelete(null)} sx={{ color: '#FFFFFF', p: 0.5 }}>
                        <CloseIcon sx={{ fontSize: '20px' }} />
                    </IconButton>
                </DialogTitle>

                {/* Content */}
                <DialogContent sx={{ padding: '24px !important', backgroundColor: '#FFFFFF' }}>
                    <Box display="flex" alignItems="center" gap={3}>
                        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <WarningImage style={{ width: 56, height: 52 }} />
                        </Box>
                        <Box textAlign="left">
                            <Typography sx={{ fontSize: '20px', fontWeight: 600, color: '#1e293b', mb: 1, lineHeight: 1.3 }}>
                                Are you sure to remove this Mapping?
                            </Typography>
                            <Typography sx={{ fontSize: '14px', color: '#64748b', fontWeight: 400 }}>
                                Once confirmed, the action can't be undone.
                            </Typography>
                        </Box>
                    </Box>
                </DialogContent>

                {/* Actions */}
                <DialogActions sx={{ justifyContent: 'flex-end', padding: '16px 28px 24px 28px', gap: 2, backgroundColor: '#FFFFFF' }}>
                    <Button
                        onClick={() => setRowToDelete(null)}
                        sx={{
                            color: '#009BD8',
                            fontWeight: 700,
                            fontSize: '14px',
                            textTransform: 'uppercase',
                            padding: '8px 16px',
                            '&:hover': { backgroundColor: 'rgba(0, 155, 216, 0.08)' }
                        }}
                    >
                        NO, I WANT IT!
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        variant="contained"
                        sx={{
                            backgroundColor: '#FF2D2D !important',
                            color: '#FFFFFF !important',
                            fontWeight: 700,
                            fontSize: '14px',
                            textTransform: 'uppercase',
                            borderRadius: '4px',
                            padding: '8px 28px',
                            minWidth: '110px',
                            boxShadow: 'none',
                            '&:hover': { backgroundColor: '#E02424 !important', boxShadow: 'none' }
                        }}
                    >
                        DELETE
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ServiceMappingTab;
